// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import resetShippingAddressFormValidation from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/ResetShippingAddressFormValidation";
import validateShippingAddressForm from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/ValidateShippingAddressForm";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import {
    AddressFieldDisplayCollectionModel,
    AddressFieldDisplayModel,
    BaseAddressModel,
    CountryModel,
    CustomerValidationDto,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import { CustomerAddressFormFieldStyles } from "@insite/content-library/Components/CustomerAddressFormFields";
import { OnePageCheckoutFormContext } from "@insite/content-library/Pages/OnePageCheckoutPage";
import { TextFieldProps } from "@insite/mobius/TextField";
import CustomerAddressFormFields from "@sc/Overrides/Components/CustomerAddressFormFields";
import { SHIPPING_FORM } from "@sc/XDL/constants";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";

interface OwnProps {
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    extendedStyles?: ShippingAddressFormStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    shippingAddressFormState: state.pages.onePageCheckout.shippingAddressFormState,
    billingAddressFormState: state.pages.onePageCheckout.billingAddressFormState,
    isBillingAddressTheSameAsShipping: state.pages.onePageCheckout.isBillingAddressTheSameAsShipping,
    billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
});

const mapDispatchToProps = {
    setAddress: (address: ShipToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/OnePageCheckout/SetShippingAddress",
            address,
        });
    },
    validateForm: makeHandlerChainAwaitable<Parameters<typeof validateShippingAddressForm>[0], boolean>(
        validateShippingAddressForm,
    ),
    resetShippingAddressFormValidation,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

type State = {
    styles: ShippingAddressFormStyles;
};

export interface ShippingAddressFormStyles {
    formFields?: CustomerAddressFormFieldStyles;
}

export const shippingAddressFormStyles: ShippingAddressFormStyles = {
    formFields: {
        countryGridItem: { width: 6 },
        cityGridItem: { width: 6 },
        stateGridItem: { width: 6 },
        postalCodeGridItem: { width: 6 },
        phoneGridItem: { width: 6 },
        emailGridItem: { width: 6 },
    },
};

const styles = shippingAddressFormStyles;
// XNG-Changes
const formId = SHIPPING_FORM;

const shouldValidate = (validation: CustomerValidationDto) =>
    validation.firstName?.isDisabled === false ||
    validation.lastName?.isDisabled === false ||
    validation.companyName?.isDisabled === false ||
    validation.attention?.isDisabled === false ||
    validation.address1?.isDisabled === false ||
    validation.address2?.isDisabled === false ||
    validation.address3?.isDisabled === false ||
    validation.address4?.isDisabled === false ||
    validation.city?.isDisabled === false ||
    validation.state?.isDisabled === false ||
    validation.country?.isDisabled === false ||
    validation.postalCode?.isDisabled === false ||
    validation.phone?.isDisabled === false ||
    validation.email?.isDisabled === false ||
    validation.fax?.isDisabled === false;

const mergeValidations = (validation1: CustomerValidationDto, validation2: CustomerValidationDto) => {
    const mergedValidation: CustomerValidationDto = {} as CustomerValidationDto;

    for (const key in validation1) {
        // eslint-disable-next-line no-prototype-builtins
        if (validation1.hasOwnProperty(key)) {
            const fieldKey = key as keyof CustomerValidationDto;
            const field1 = validation1[fieldKey];
            const field2 = validation2[fieldKey];
            if (!field1 || !field2) {
                continue;
            }

            mergedValidation[fieldKey] = {
                isDisabled: field1.isDisabled,
                isRequired: field1.isRequired || field2.isRequired,
                maxLength: field1.maxLength,
            };
        }
    }

    return mergedValidation;
};

const mergeFieldDisplays = (
    fieldDisplay1: AddressFieldDisplayCollectionModel,
    fieldDisplay2: AddressFieldDisplayCollectionModel,
) => {
    const mergedFieldDisplay: AddressFieldDisplayCollectionModel = {} as AddressFieldDisplayCollectionModel;

    for (const key in fieldDisplay1) {
        // eslint-disable-next-line no-prototype-builtins
        if (fieldDisplay1.hasOwnProperty(key)) {
            const fieldKey = key as keyof Omit<AddressFieldDisplayCollectionModel, "uri" | "properties">;
            const field1 = fieldDisplay1[fieldKey];
            const field2 = fieldDisplay2[fieldKey];
            if (!field1 || !field2) {
                continue;
            }

            mergedFieldDisplay[fieldKey] = {
                displayName: field1.displayName,
                isVisible: field1.isVisible || field2.isVisible,
            } as AddressFieldDisplayModel;
        }
    }

    return mergedFieldDisplay;
};

class ShippingAddressForm extends React.Component<Props, State> {
    static contextType = OnePageCheckoutFormContext;
    declare context: React.ContextType<typeof OnePageCheckoutFormContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            styles: mergeToNew(styles, this.props.extendedStyles),
        };
    }

    componentDidMount() {
        const {
            shippingAddressFormState,
            billingAddressFormState,
            isBillingAddressTheSameAsShipping,
            billToAddressFields,
            validateForm,
        } = this.props;

        if (!shippingAddressFormState) {
            return;
        }

        if (isBillingAddressTheSameAsShipping && (!billingAddressFormState || !billToAddressFields)) {
            return;
        }

        let fieldDisplay = this.props.fieldDisplay;
        if (isBillingAddressTheSameAsShipping && billToAddressFields) {
            fieldDisplay = mergeFieldDisplays(fieldDisplay, billToAddressFields);
        }

        const { address } = shippingAddressFormState;
        let validation = address.validation;
        if (!validation) {
            return;
        }

        if (isBillingAddressTheSameAsShipping && billingAddressFormState) {
            validation = mergeValidations(validation, billingAddressFormState.address.validation!);
        }

        if (shouldValidate(validation)) {
            this.context.validators.shippingAddress = () =>
                validateForm({
                    address,
                    validation,
                    fieldDisplay,
                });
            this.props.resetShippingAddressFormValidation();
        }
    }

    componentWillUnmount() {
        this.context.validators.shippingAddress = undefined;
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { shippingAddressFormState: prevShippingAddressFormState, fieldDisplay: prevFieldDisplay } = prevProps;
        const {
            shippingAddressFormState,
            billingAddressFormState,
            isBillingAddressTheSameAsShipping,
            billToAddressFields,
            validateForm,
        } = this.props;
        if (prevShippingAddressFormState?.address !== shippingAddressFormState?.address && shippingAddressFormState) {
            let fieldDisplay = this.props.fieldDisplay;
            if (isBillingAddressTheSameAsShipping && billToAddressFields) {
                fieldDisplay = mergeFieldDisplays(fieldDisplay, billToAddressFields);
            }

            const { address } = shippingAddressFormState;
            let validation = address.validation;

            if (
                (!prevShippingAddressFormState ||
                    prevShippingAddressFormState.address !== address ||
                    prevShippingAddressFormState.address.validation !== validation ||
                    prevFieldDisplay !== fieldDisplay) &&
                validation
            ) {
                if (isBillingAddressTheSameAsShipping && billingAddressFormState) {
                    validation = mergeValidations(validation, billingAddressFormState.address.validation!);
                }

                if (shouldValidate(validation)) {
                    this.context.validators.shippingAddress = () =>
                        validateForm({
                            address,
                            validation: validation!,
                            fieldDisplay,
                        });

                    if (this.context.formSubmitAttempted) {
                        validateForm({
                            address,
                            validation,
                            fieldDisplay,
                        });
                    }
                } else {
                    this.context.validators.shippingAddress = undefined;
                }
            }
        }
    }

    handleInputChange: TextFieldProps["onChange"] = event => {
        const { shippingAddressFormState, setAddress } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const { address } = shippingAddressFormState;
        setAddress({
            ...address,
            [event.target.name]: event.target.value,
        });
    };

    handleCountryChange = (_: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => {
        const { shippingAddressFormState, setAddress } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const { address } = shippingAddressFormState;
        setAddress({
            ...address,
            country,
        });
    };

    handleStateChange = (_: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => {
        const { shippingAddressFormState, setAddress } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const { address } = shippingAddressFormState;
        setAddress({
            ...address,
            state,
        });
    };

    render() {
        const {
            shippingAddressFormState,
            billingAddressFormState,
            isBillingAddressTheSameAsShipping,
            countries,
            billToAddressFields,
        } = this.props;
        const { styles } = this.state;
        if (!shippingAddressFormState) {
            return null;
        }
        const { address, formErrors } = shippingAddressFormState;
        const {
            firstName,
            lastName,
            attention,
            companyName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
        } = address;

        let fieldDisplay = this.props.fieldDisplay;
        if (isBillingAddressTheSameAsShipping && billToAddressFields) {
            fieldDisplay = mergeFieldDisplays(fieldDisplay, billToAddressFields);
        }

        let validation = address.validation;
        if (!validation) {
            return null;
        }

        if (isBillingAddressTheSameAsShipping && billingAddressFormState) {
            validation = mergeValidations(validation, billingAddressFormState.address.validation!);
        }

        return (
            <CustomerAddressFormFields
                firstName={firstName}
                lastName={lastName}
                attention={attention}
                companyName={companyName}
                address1={address1}
                address2={address2}
                address3={address3}
                address4={address4}
                country={country}
                city={city}
                state={state}
                postalCode={postalCode}
                phone={phone}
                email={email}
                fax={fax}
                onChangeFirstName={this.handleInputChange}
                onChangeLastName={this.handleInputChange}
                onChangeAttention={this.handleInputChange}
                onChangeCompanyName={this.handleInputChange}
                onChangeAddress1={this.handleInputChange}
                onChangeAddress2={this.handleInputChange}
                onChangeAddress3={this.handleInputChange}
                onChangeAddress4={this.handleInputChange}
                onChangeCountry={this.handleCountryChange}
                onChangeState={this.handleStateChange}
                onChangeCity={this.handleInputChange}
                onChangePostalCode={this.handleInputChange}
                onChangePhone={this.handleInputChange}
                onChangeEmail={this.handleInputChange}
                onChangeFax={this.handleInputChange}
                firstNameError={formErrors.firstName}
                lastNameError={formErrors.lastName}
                attentionError={formErrors.attention}
                companyNameError={formErrors.companyName}
                address1Error={formErrors.address1}
                address2Error={formErrors.address2}
                address3Error={formErrors.address3}
                address4Error={formErrors.address4}
                countryError={formErrors.country}
                stateError={formErrors.state}
                cityError={formErrors.city}
                postalCodeError={formErrors.postalCode}
                phoneError={formErrors.phone}
                emailError={formErrors.email}
                faxError={formErrors.fax}
                countries={countries}
                validation={validation}
                fieldDisplay={fieldDisplay}
                extendedStyles={styles.formFields}
                hideAdditionalAddresses={true}
                // XNG-Changes
                formId={formId}
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ShippingAddressForm);
